import { useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";

import CommonBanner from "component/Common/CommonBanner";
import { formatPrice } from "helpers/common";
import { BiPrinter } from "react-icons/bi";
import { BsWallet2 } from "react-icons/bs";
import epayment from "assets/img/payment/epayment.jpg";
import { Image } from "react-bootstrap";

const PaymentReceiptPage = () => {
    const { t } = useTranslation();
    const location = useLocation();
    const navigate = useNavigate();
    const receiptRef = useRef(null);

    const state = location.state;
    const handlePrint = () => {
        const receiptNode = receiptRef.current;
        if (!receiptNode) return;

        const printNode = receiptNode.cloneNode(true);
        printNode.querySelectorAll(".no-print").forEach((el) => el.remove());

        const iframe = document.createElement("iframe");
        iframe.style.position = "fixed";
        iframe.style.right = "0";
        iframe.style.bottom = "0";
        iframe.style.width = "0";
        iframe.style.height = "0";
        iframe.style.border = "0";
        document.body.appendChild(iframe);

        const doc = iframe.contentWindow.document;
        doc.open();
        doc.write(`
        <!DOCTYPE html>
        <html>
            <head>
                <meta charset="utf-8" />
                <title>receipt</title>
                <style>
                    * {
                        -webkit-print-color-adjust: exact;
                        print-color-adjust: exact;
                        color-adjust: exact;
                        box-sizing: border-box;
                    }
                    body {
                        font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, Arial, sans-serif;
                        margin: 0;
                        padding: 24px;
                    }
                    .d-flex { display: flex; }
                    .align-items-center { align-items: center; }
                    .justify-content-center { justify-content: center; }
                    .mx-auto { margin-left: auto; margin-right: auto; }
                    .badge {
                        display: inline-block;
                        border-radius: 6px;
                        font-size: 14px;
                        font-weight: 600;
                    }
                    .row {
                        display: flex;
                        flex-wrap: wrap;
                        margin: 0 -12px;
                    }
                    .col-md-6 {
                        width: 50%;
                        padding: 0 12px;
                        box-sizing: border-box;
                        margin-bottom: 16px;
                    }
                    .text-muted { color: #6c757d; }
                    .text-center { text-align: center; }
                    .fw-semibold { font-weight: 600; }
                    .small { font-size: 14px; }
                    .rounded-4 { border-radius: 16px; }
                    .rounded-3 { border-radius: 12px; }
                    .rounded-circle { border-radius: 50%; }
                    .overflow-hidden { overflow: hidden; }
                    @page {
                        size: auto;
                        margin: 12mm;
                    }
                </style>
            </head>
            <body>
                ${printNode.outerHTML}
            </body>
        </html>
    `);
        doc.close();

        iframe.onload = () => {
            iframe.contentWindow.focus();
            iframe.contentWindow.print();
            setTimeout(() => {
                document.body.removeChild(iframe);
            }, 500);
        };
    };
    if (!state || !state.payment || !state.booking) {
        return (
            <>
                <CommonBanner
                    heading={t("paymentReceipt")}
                    pagination="pagination"
                />

                <section className="py-5">
                    <div className="container">
                        <div
                            className="mx-auto text-center bg-white rounded-4 shadow-sm p-5"
                            style={{ maxWidth: "600px" }}
                        >
                            <div
                                className="mx-auto mb-4 d-flex align-items-center justify-content-center rounded-circle"
                                style={{
                                    width: "80px",
                                    height: "80px",
                                    background: "#fff3cd",
                                    fontSize: "36px",
                                }}
                            >
                                !
                            </div>

                            <h2 className="mb-3">{t("receiptUnavailable")}</h2>

                            <p className="text-muted mb-4">
                                {t("receiptUnavailableDescription")}
                            </p>

                            <button
                                type="button"
                                className="btn btn-primary px-4"
                                onClick={() => navigate("/")}
                            >
                                {t("backToHome")}
                            </button>
                        </div>
                    </div>
                </section>
            </>
        );
    }

    const { payment, booking } = state;

    const isSuccess = payment?.status === "CONFIRMED";

    return (
        <>
            <CommonBanner
                heading={t("paymentReceipt")}
                pagination="pagination"
            />

            <section
                className="py-5"
                style={{
                    background: "#f7f8fa",
                    minHeight: "calc(100vh - 200px)",
                }}
            >
                <div className="container">
                    <div className="d-flex justify-content-end max-w-3xl mx-auto mb-3">
                        <button
                            type="button"
                            className="btn btn-primary"
                            onClick={handlePrint}
                        >
                            <div className="flex gap-2 items-center">
                                <BiPrinter />
                                {t("print")}
                            </div>
                        </button>
                    </div>

                    <div
                        ref={receiptRef}
                        className="mx-auto bg-white rounded-4  max-w-3xl shadow-sm overflow-hidden"
                    >
                        <div
                            className="text-center px-4 py-5"
                            style={{
                                background: isSuccess ? "#f0fff4" : "#fff5f5",
                            }}
                        >
                            <div
                                className="mx-auto mb-3 d-flex align-items-center justify-content-center rounded-circle"
                                style={{
                                    width: "72px",
                                    height: "72px",
                                    background: isSuccess
                                        ? "#28a745"
                                        : "#dc3545",
                                    color: "#fff",
                                    fontSize: "34px",
                                    fontWeight: 600,
                                }}
                            >
                                {isSuccess ? "✓" : "!"}
                            </div>

                            <h2 className="mb-2">
                                {isSuccess
                                    ? t("paymentSuccessful")
                                    : t("paymentFailed")}
                            </h2>

                            <p className="text-muted mb-0">
                                {isSuccess
                                    ? t("paymentSuccessfulDescription")
                                    : payment?.ErrorMessage}
                            </p>
                        </div>

                        <div className="text-center px-4 py-4 border-bottom">
                            <div className="text-muted mb-2">
                                {t("amountPaid")}
                            </div>

                            <div
                                style={{
                                    fontSize: "36px",
                                    fontWeight: 700,
                                    color: "#212529",
                                }}
                            >
                                {formatPrice(booking?.amount)}
                            </div>
                        </div>

                        <div className="p-4">
                            <h5 className="mb-4">{t("paymentDetails")}</h5>

                            <div className="row g-4">
                                <div className="col-md-6">
                                    <div className="text-muted small mb-1">
                                        {t("orderNumber")}
                                    </div>
                                    <div className="fw-semibold">
                                        {payment?.OrderNumber}
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <span className="text-muted small mb-1">
                                        <div className="flex gap-1 items-center">
                                            <BsWallet2 className=" fa-fw me-2" />
                                            {t("paymentMethod")}:
                                        </div>
                                    </span>
                                    <span className="fw-semibold">
                                        CIB/DAHABIA{"  "}
                                        <Image
                                            className="w-10 inline-block mr-2"
                                            loading="lazy"
                                            src={epayment}
                                            alt="e-payment"
                                        />
                                    </span>
                                </div>
                                <div className="col-md-6">
                                    <div className="text-muted small mb-1">
                                        {t("paymentDate")}
                                    </div>
                                    <div className="fw-semibold">
                                        {payment?.timestamp}
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="text-muted small mb-1">
                                        {t("cardholder")}
                                    </div>
                                    <div className="fw-semibold">
                                        {payment?.cardholderName}
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="text-muted small mb-1">
                                        {t("cardNumber")}
                                    </div>
                                    <div className="fw-semibold">
                                        {payment?.Pan}
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="text-muted small mb-1">
                                        {t("approvalCode")}
                                    </div>
                                    <div className="fw-semibold">
                                        {payment?.approvalCode}
                                    </div>
                                </div>

                                <div className="col-md-6">
                                    <div className="text-muted small mb-1">
                                        {t("paymentStatus")}
                                    </div>
                                    <span
                                        className="badge px-3 py-2"
                                        style={{
                                            background: isSuccess
                                                ? "#d1e7dd"
                                                : "#f8d7da",
                                            color: isSuccess
                                                ? "#0f5132"
                                                : "#842029",
                                        }}
                                    >
                                        {payment?.status}
                                    </span>
                                </div>
                            </div>
                        </div>

                        <div
                            className="mx-4 mb-4 p-4 rounded-3"
                            style={{ background: "#f8f9fa" }}
                        >
                            <h5 className="mb-3">{t("paymentInformation")}</h5>

                            <div className="mb-3">
                                <div className="text-muted small">
                                    {t("description")}
                                </div>
                                <div className="fw-semibold">
                                    {booking?.description}
                                </div>
                            </div>

                            {booking?.client_email && (
                                <div className="mb-3">
                                    <div className="text-muted small">
                                        {t("email")}
                                    </div>
                                    <div className="fw-semibold">
                                        {booking?.client_email}
                                    </div>
                                </div>
                            )}

                            <div>
                                <div className="text-muted small">
                                    {t("paymentLinkStatus")}
                                </div>
                                <div className="fw-semibold text-capitalize">
                                    {booking?.status}
                                </div>
                            </div>
                        </div>

                        <div className="px-4 py-4 border-top text-center no-print">
                            <p className="text-muted small mb-3">
                                {t("keepReceipt")}
                            </p>

                            <button
                                type="button"
                                className="btn btn-primary px-5"
                                onClick={() => navigate("/", { state: null })}
                            >
                                {t("backToHome")}
                            </button>
                        </div>
                    </div>
                </div>
            </section>
        </>
    );
};

export default PaymentReceiptPage;
